<template>
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 px-4"
    @click.self="discard"
    @mousedown.stop
    @mouseup.stop
  >
    <div
      class="custom_dialog rounded-xl bg-white"
      id="itemdialogs"
      style="width: 800px; overflow: auto; min-height: 85%; max-height: 85%;"
      @click.stop
    >
      <div class="relative bg-screen_bg" :style="`min-height: ${dialogHeight}px;`">
        <div>
          <div class="bg-primary flex justify-between items-center p-3">
            <div class="font-medium text-white heading-3">Items</div>
            <button @click="discard" class="text-white hover:text-gray-200 focus:outline-none">
              <i class="fa-solid fa-times text-2xl"></i>
            </button>
          </div>

          <div class="px-4 pt-4">
            <div class="w-72">
              <SearchTextInput @searchCall="searchCall" :placeholderText="`Search...`"/>
            </div>
          </div>

          <div v-if="itemsDataList.length > 0" class="p-4">
            <div v-for="(item, i) in itemsDataList" :key="item.itemMasterId || i" class="card rounded-xl bg-white mb-2 p-4">
              <div class="text-text1 flex justify-between mb-1">
                <div>
                  <p>{{ item.itemName }}</p>
                  <p v-if="parseInt(item.qnty) > 0" class="text-gray4 heading-7">
                    Default Price: {{ item.defaultPrice }}
                  </p>
                </div>
                <p v-if="parseInt(item.qnty) === 0">
                  {{ item.itemPrice.toFixed(2) | amountOnlyNumber }}
                </p>
                <div v-else>
                  <TextField
                    :inputId="`itmprices ${i}`"
                    :inputext="item.itemPrice"
                    :placholderText="``"
                    :lableText="''"
                    :inputType="'number'"
                    :textRight="false"
                    :autoFocus="false"
                    :setReadOnly="false"
                    :inputTypeText="'number'"
                    @inputChangeAction="(data) => item.itemPrice = data"
                  />
                </div>
              </div>

              <div class="sm:flex justify-between">
                <div class="mb-2 sm:mb-0">
                  <p class="mb-1 text-gray4 heading-7">Code: {{ item.itemCode }}, Unit: {{ item.itemUnit }}</p>
                  <p class="text-gray4 heading-7">/{{ item.categoryName }}</p>
                </div>
                <div class="flex justify-end items-center gap-4">
                  <Button
                    class="py-1.5"
                    v-if="item.qnty > 0"
                    :btnBorder="true"
                    :btnSize="'small'"
                    :textColor="'text1'"
                    :btnColor="'white'"
                    :btnText="'Delete'"
                    @buttonAction="deleteItem(i, item)"
                  />
                  <button
                    v-if="item.qnty > 0"
                    @click="minusQnty(i, item)"
                    :disabled="item.qnty === 1"
                    :class="item.qnty > 1 ? 'text-primary' : 'text-white'"
                    class="outline outline-1 w-fit bg-info outline-gray-200 outline-gray rounded py-2 px-2.5 shadow flex justify-center items-center"
                  >
                    <i class="fa fa-minus heading-5"></i>
                  </button>
                  <div v-if="item.qnty > 0" class="text-text1 flex items-center">{{ item.qnty }}</div>
                  <button
                    @click="addQnty(i, item)"
                    class="bg-info text-primary outline outline-1 w-fit outline-gray-200 outline-gray rounded py-1 px-2.5 shadow"
                  >
                    <i class="fa fa-plus heading-5"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="px-4">
            <NoRecords :alertMessage="`No records found.`" />
          </div>
        </div>

        <!-- Footer Buttons -->
        <div class="sticky -bottom-1 bg-screen_bg w-full flex justify-end pr-3 pt-3">
          <div class="text-right flex gap-2 mb-3">
            <Button
              :btnSize="'medium'"
              :textColor="'text1'"
              :btnColor="'gray1'"
              :btnText="'Cancel'"
              @buttonAction="discard()"
            />
            <Button
              :btnSize="'medium'"
              :textColor="'white'"
              :btnColor="'success'"
              :btnText="isSaving ? 'Saving...' : 'Save'"
              :disabled="isSaving"
              @buttonAction="saveItems()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from '@/View/components/globalButton.vue'
import SearchTextInput from '@/View/components/searchTextInput.vue'
import NoRecords from '@/View/components/noRecords.vue'
import TextField from '@/View/components/textfield.vue'
import MyJobApp from '@/api/MyJobApp.js'
import deboucneMixin from '@/mixins/debounce.js'

export default {
  name: 'ItemSelection',
  components: { NoRecords, Button, SearchTextInput, TextField },
  mixins: [deboucneMixin],
  props: {
    itemsSelected: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      deletedItems: [],
      selectedItemList: [],
      itemsDataList: [],
      searchItemText: '',
      dialogHeight: 500,
      getItems: () => {},
      isSaving: false  // Prevent double-save
    }
  },
  created() {
    this.selectedItemList = JSON.parse(JSON.stringify(this.itemsSelected || []))
  },
  mounted() {
    // Set loader before API call
    this.$store.dispatch('SetLoader', { status: true, message: 'Loading items...' })

    // Delay initial load to ensure DOM is ready
    this.$nextTick(() => {
      this.itemListGet()
      const el = document.getElementById('itemdialogs')
      if (el) this.dialogHeight = el.offsetHeight
    })

    this.getItems = this.debounce(function () {
      this.itemListGet()
    }, 500)
  },
  beforeDestroy() {
    // Ensure loader is turned off when component is destroyed
    this.$store.dispatch('SetLoader', { status: false, message: '' })
  },
  methods: {
    deleteItem(index, data) {
      this.itemsDataList[index].qnty = 0
      const fIndex = this.selectedItemList.findIndex(x => x.itemMasterId === data.itemMasterId)
      if (fIndex >= 0) {
        const removed = this.selectedItemList.splice(fIndex, 1)
        this.deletedItems.push(removed[0])
      }
    },
    minusQnty(index, data) {
      this.itemsDataList[index].qnty = data.qnty - 1
      const fIndex = this.selectedItemList.findIndex(x => x.itemMasterId === data.itemMasterId)
      if (fIndex >= 0) {
        this.selectedItemList[fIndex].itemAmount = data.itemPrice * this.itemsDataList[index].qnty
        this.selectedItemList[fIndex].itemQty = this.itemsDataList[index].qnty
      }
    },
    addQnty(index, data) {
      this.itemsDataList[index].qnty += 1
      const taxList = JSON.parse(data.itemTaxes || '{"itemTaxList": null}')
      const fIndex = this.selectedItemList.findIndex(x => x.itemMasterId === data.itemMasterId)

      if (fIndex >= 0) {
        this.selectedItemList[fIndex].itemAmount = data.itemPrice * this.itemsDataList[index].qnty
        this.selectedItemList[fIndex].itemQty = this.itemsDataList[index].qnty
      } else {
        const obj = {
          itemTransactionId: 0,
          itemMappingId: 0,
          itemMasterId: data.itemMasterId,
          itemCode: data.itemCode,
          itemName: data.itemName,
          itemDescription: data.itemDescription,
          itemPrice: data.itemPrice,
          itemQty: this.itemsDataList[index].qnty,
          itemUnit: data.itemUnit,
          itemAmount: data.itemPrice * this.itemsDataList[index].qnty,
          isTaxable: taxList.itemTaxList !== null,
          taxRateList: taxList.itemTaxList || []
        }
        this.selectedItemList.push(obj)
      }
    },
    searchCall(data) {
      this.searchItemText = data
      this.getItems()
    },
    discard() {
      // Keep old behavior
      this.$root.$emit('itemSelectionHandler', false, null)
      // Also emit close for new behavior
      this.$emit('close')
    },
    saveItems() {
      // Prevent double-clicking save button
      if (this.isSaving) {
        console.log('Save already in progress, ignoring duplicate click')
        return
      }

      this.isSaving = true

      try {
        // Update prices from edited input fields
        this.selectedItemList.forEach(e => {
          const fIndex = this.itemsDataList.findIndex(x => x.itemMasterId === e.itemMasterId)
          if (fIndex >= 0) {
            console.log("Price updated →", this.itemsDataList[fIndex].itemPrice, '×', e.itemQty)
            e.itemPrice = parseFloat(this.itemsDataList[fIndex].itemPrice) || 0
            e.itemAmount = e.itemPrice * e.itemQty
          }
        })

        // Keep your old $root emit (so nothing breaks elsewhere)
        this.$root.$emit('itemSelectionHandler', true, this.selectedItemList, this.deletedItems)

        // ALSO emit modern Vue way so modal closes properly
        this.$emit('update:itemsSelected', this.selectedItemList)

        // Use nextTick to ensure emission completes before closing
        this.$nextTick(() => {
          this.$emit('close')
          this.isSaving = false
        })
      } catch (error) {
        console.error('Error saving items:', error)
        this.isSaving = false
      }
    },
    itemListGet() {
      // Set a timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        this.$store.dispatch('SetLoader', { status: false, message: '' })
        console.error('Item list loading timed out')
      }, 10000) // 10 second timeout

      MyJobApp.getItemsList(
        0, 0, this.searchItemText, 'addedDate', 'DESC', true, 0, true,
        response => {
          clearTimeout(loadingTimeout)

          let tempList = []
          if (response.Data && response.Data.tableRow !== null) {
            response.Data.tableRow.forEach(data => {
              tempList.push({ ...data, qnty: 0, defaultPrice: data.itemPrice })
            })
          }

          this.$nextTick(() => {
            if (this.selectedItemList.length > 0 && tempList.length > 0) {
              this.selectedItemList.forEach(element => {
                const fIndex = tempList.findIndex(x => x.itemMasterId === element.itemMasterId)
                if (fIndex >= 0) {
                  tempList[fIndex].qnty = element.itemQty
                  tempList[fIndex].itemPrice = element.itemPrice
                }
              })
            }
            this.itemsDataList = tempList
          })

          this.$store.dispatch('SetLoader', { status: false, message: '' })
        },
        (error) => {
          clearTimeout(loadingTimeout)
          console.error('Failed to load items:', error)
          this.$store.dispatch('SetLoader', { status: false, message: '' })
        }
      )
    }
  }
}
</script>